import React, { useState, useMemo } from 'react';
import { 
  TrendingUp, 
  Layers, 
  ArrowLeft, 
  DollarSign, 
  CheckCircle2, 
  Clock, 
  XCircle, 
  FileText, 
  Zap, 
  Calendar, 
  Users, 
  PieChart, 
  ChevronRight,
  Plus,
  Search,
  AlertTriangle,
  Send,
  Copy,
  Wrench,
  RotateCcw,
  Check,
  Filter
} from 'lucide-react';
import { formatCurrencyBRL } from '../../../utils/formatUtils';
import WorkPipelineKanban, { PipelineItem } from './WorkPipelineKanban';
import ScreenTourButton from '../../../components/tour/ScreenTourButton';

export interface ManagementDashboardHybridProps {
  items: PipelineItem[];
  onMoveStage: (itemId: string, newStage: PipelineItem['stage']) => Promise<void> | void;
  onOpenItem?: (item: PipelineItem) => void;
  onOpenWorkOrder?: (itemId: string) => void;
  onOpenReceipt?: (itemId: string) => void;
  onCreateNewQuote?: () => void;
  onConvertToWorkOrder?: (item: PipelineItem) => void;
  onDuplicateQuote?: (item: PipelineItem) => void;
  onRecalculateQuoteDrift?: (item: PipelineItem) => void;
  onBack: () => void;
}

export default function ManagementDashboardHybrid({
  items,
  onMoveStage,
  onOpenItem,
  onOpenWorkOrder,
  onOpenReceipt,
  onCreateNewQuote,
  onConvertToWorkOrder,
  onDuplicateQuote,
  onRecalculateQuoteDrift,
  onBack
}: ManagementDashboardHybridProps) {
  const [viewMode, setViewMode] = useState<'kanban' | 'table' | 'metrics'>('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Cálculos de Métricas
  const totalQuotes = items.length;
  const approvedQuotes = items.filter(i => ['approved', 'in_progress', 'completed', 'paid'].includes(i.stage));
  const conversionRate = totalQuotes > 0 ? (approvedQuotes.length / totalQuotes) * 100 : 0;
  
  const totalPipelineValue = items.reduce((acc, i) => acc + i.finalPrice, 0);
  const totalApprovedValue = approvedQuotes.reduce((acc, i) => acc + i.finalPrice, 0);
  const averageTicket = totalQuotes > 0 ? totalPipelineValue / totalQuotes : 0;

  // Filtragem para a visão de tabela
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = (item.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (item.clientName || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item.stage === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [items, searchTerm, statusFilter]);

  const handleSendWhatsApp = (item: PipelineItem) => {
    const text = encodeURIComponent(
      `Olá, ${item.clientName}! 👋 Segue a proposta comercial para "${item.title}" no valor de ${formatCurrencyBRL(item.finalPrice)}. Ficamos à disposição para iniciar a execução!`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const handleCopyLink = (item: PipelineItem) => {
    const dummyLink = `${window.location.origin}/proposta/${item.id}`;
    navigator.clipboard.writeText(dummyLink);
    setCopiedId(item.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getStageLabel = (stage: string) => {
    switch (stage) {
      case 'lead': return { label: 'Rascunho', color: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
      case 'proposal': return { label: 'Enviado', color: 'bg-amber-50 text-amber-700 border-amber-200' };
      case 'approved': return { label: 'Aprovado', color: 'bg-emerald-50 text-brand border-emerald-200' };
      case 'in_progress': return { label: 'Em Execução', color: 'bg-blue-50 text-blue-700 border-blue-200' };
      case 'completed': return { label: 'Concluído', color: 'bg-purple-50 text-purple-700 border-purple-200' };
      case 'paid': return { label: 'Quitado / Pago', color: 'bg-teal-50 text-teal-800 border-teal-200' };
      default: return { label: stage, color: 'bg-zinc-100 text-zinc-600 border-zinc-200' };
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 pb-20 font-sans">
      {/* Header Sticky Slim (56px) - Padrão R-MAX Mobile */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-zinc-200/80 px-4 h-14 flex items-center justify-between shadow-2xs">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onBack}
            className="p-2 -ml-1 text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-xl transition-all active:scale-95 cursor-pointer shrink-0"
            title="Voltar"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-black text-zinc-900 truncate tracking-tight">
              Vendas & Propostas
            </h1>
            <p className="text-[11px] text-zinc-500 truncate font-medium">
              {totalQuotes} {totalQuotes === 1 ? 'orçamento no funil' : 'orçamentos no funil'}
            </p>
          </div>
        </div>

        {/* Alternador de 3 Visões & Botão de Ajuda */}
        <div className="flex items-center gap-1.5 shrink-0">
          <ScreenTourButton screenKey="kanban" />
          <div className="flex bg-zinc-100 p-1 rounded-2xl text-xs font-bold">
            <button
              type="button"
              onClick={() => setViewMode('kanban')}
              className={`px-2.5 sm:px-3 py-1.5 rounded-xl transition-all cursor-pointer flex items-center gap-1 active:scale-95 ${
                viewMode === 'kanban' ? 'bg-brand text-white font-black shadow-xs' : 'text-zinc-600 hover:text-zinc-900'
              }`}
              title="Funil Kanban"
            >
              <Layers className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Funil</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`px-2.5 sm:px-3 py-1.5 rounded-xl transition-all cursor-pointer flex items-center gap-1 active:scale-95 ${
                viewMode === 'table' ? 'bg-brand text-white font-black shadow-xs' : 'text-zinc-600 hover:text-zinc-900'
              }`}
              title="Lista de Propostas"
            >
              <FileText className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Lista</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('metrics')}
              className={`px-2.5 sm:px-3 py-1.5 rounded-xl transition-all cursor-pointer flex items-center gap-1 active:scale-95 ${
                viewMode === 'metrics' ? 'bg-brand text-white font-black shadow-xs' : 'text-zinc-600 hover:text-zinc-900'
              }`}
              title="Métricas"
            >
              <PieChart className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Métricas</span>
            </button>
          </div>

          {onCreateNewQuote && (
            <button
              type="button"
              onClick={onCreateNewQuote}
              className="p-2 sm:px-3.5 sm:py-2 bg-brand hover:bg-brand-dark text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-sm shadow-brand/20 flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all shrink-0"
              title="Novo Orçamento"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo</span>
            </button>
          )}
        </div>
      </header>

      {/* Conteúdo Principal */}
      <div className="py-6">
        {/* ──────────────────────────────────────────────────────────────────── */}
        {/* VISÃO 1: FUNIL KANBAN OPERACIONAL                                    */}
        {/* ──────────────────────────────────────────────────────────────────── */}
        {viewMode === 'kanban' && (
          <div className="px-4 sm:px-6">
            <WorkPipelineKanban
              items={items}
              onMoveStage={onMoveStage}
              onOpenItem={onOpenItem}
              onOpenWorkOrder={onOpenWorkOrder}
              onOpenReceipt={onOpenReceipt}
              onNewQuote={onCreateNewQuote}
            />
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────── */}
        {/* VISÃO 2: TABELA DE GESTÃO COM DRIFT DE INSUMOS & ONE-CLICK OS        */}
        {/* ──────────────────────────────────────────────────────────────────── */}
        {viewMode === 'table' && (
          <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-4 text-xs">
            {/* Barra de Busca e Filtro de Status */}
            <div className="bg-white p-4 rounded-3xl border border-zinc-100 shadow-sm flex flex-col sm:flex-row items-center gap-3">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Buscar proposta por cliente ou título..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-100 rounded-xl pl-10 pr-4 py-2 text-zinc-900 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-brand/20"
                />
              </div>

              <div className="flex items-center gap-1.5 w-full sm:w-auto overflow-x-auto pb-1 sm:pb-0">
                {['all', 'proposal', 'approved', 'in_progress', 'completed'].map(st => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setStatusFilter(st)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap cursor-pointer transition-all active:scale-95 ${
                      statusFilter === st
                        ? 'bg-brand text-white font-black'
                        : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'
                    }`}
                  >
                    {st === 'all' ? 'Todos' : st === 'proposal' ? 'Enviados' : st === 'approved' ? 'Aprovados' : st === 'in_progress' ? 'Em OS' : 'Concluídos'}
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de Propostas com Ações 1-Toque */}
            <div className="bg-white rounded-3xl border border-zinc-100 overflow-hidden shadow-sm divide-y divide-zinc-100">
              {filteredItems.length === 0 ? (
                <div className="p-12 text-center text-zinc-400 font-medium space-y-2">
                  <FileText className="h-8 w-8 mx-auto text-zinc-300" />
                  <p>Nenhuma proposta encontrada com os filtros atuais.</p>
                </div>
              ) : (
                filteredItems.map(item => {
                  const stageInfo = getStageLabel(item.stage);
                  const hasDriftAlert = item.id.includes('2') || item.id.includes('4'); // Simulação de drift de materiais

                  return (
                    <div key={item.id} className="p-4 sm:p-5 hover:bg-zinc-50/50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-4">
                      {/* Dados Principais */}
                      <div className="space-y-1 min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-black text-zinc-900 text-xs sm:text-sm truncate">{item.title}</h4>
                          <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-full border ${stageInfo.color}`}>
                            {stageInfo.label}
                          </span>
                        </div>
                        <p className="text-[11px] text-zinc-500 font-medium">
                          Cliente: <strong className="text-zinc-800">{item.clientName}</strong> • {item.updatedAt}
                        </p>

                        {/* Alerta de Drift de Insumos */}
                        {hasDriftAlert && (
                          <div className="mt-2 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1 rounded-xl text-[11px] font-medium">
                            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                            <span>Os materiais ficaram mais caros no mercado. Revise o orçamento.</span>
                            {onRecalculateQuoteDrift && (
                              <button
                                type="button"
                                onClick={() => onRecalculateQuoteDrift(item)}
                                className="font-bold underline hover:text-amber-950 cursor-pointer"
                              >
                                Ajustar Preço dos Materiais
                              </button>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Preço & Ações em 1 Toque */}
                      <div className="flex items-center justify-between md:justify-end gap-3 shrink-0 pt-2 md:pt-0 border-t md:border-t-0 border-zinc-100">
                        <div className="text-left md:text-right">
                          <span className="text-[10px] text-zinc-400 font-bold block">Valor Proposta</span>
                          <strong className="text-base font-black text-brand">{formatCurrencyBRL(item.finalPrice)}</strong>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {/* Botão One-Click Converter em OS */}
                          {onConvertToWorkOrder && (
                            <button
                              type="button"
                              onClick={() => onConvertToWorkOrder(item)}
                              title="Aprovar e Gerar Ordem de Serviço"
                              className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-brand rounded-xl font-bold text-xs border border-emerald-100 flex items-center gap-1 cursor-pointer active:scale-95 transition-all"
                            >
                              <Wrench className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Gerar Ordem de Serviço</span>
                            </button>
                          )}

                          {/* Botão Enviar WhatsApp */}
                          <button
                            type="button"
                            onClick={() => handleSendWhatsApp(item)}
                            title="Enviar Proposta no WhatsApp"
                            className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl cursor-pointer active:scale-95 transition-all"
                          >
                            <Send className="h-3.5 w-3.5 text-brand" />
                          </button>

                          {/* Botão Copiar Link */}
                          <button
                            type="button"
                            onClick={() => handleCopyLink(item)}
                            title="Copiar Link da Proposta"
                            className="p-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-xl cursor-pointer active:scale-95 transition-all"
                          >
                            {copiedId === item.id ? <Check className="h-3.5 w-3.5 text-brand" /> : <Copy className="h-3.5 w-3.5 text-zinc-500" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* ──────────────────────────────────────────────────────────────────── */}
        {/* VISÃO 3: MÉTRICAS FINANCEIRAS & DASHBOARD ANALÍTICO                  */}
        {/* ──────────────────────────────────────────────────────────────────── */}
        {viewMode === 'metrics' && (
          <div className="max-w-4xl mx-auto px-4 sm:px-6 space-y-6 text-xs">
            {/* 4 Cards de Métricas Principais */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
              <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-1">
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Faturamento Previsto</span>
                <h3 className="text-lg font-black text-zinc-900">{formatCurrencyBRL(totalPipelineValue)}</h3>
                <p className="text-[10px] text-zinc-500 font-medium">{totalQuotes} propostas no total</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-1">
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Aprovados / Em Execução</span>
                <h3 className="text-lg font-black text-brand">{formatCurrencyBRL(totalApprovedValue)}</h3>
                <p className="text-[10px] text-brand font-bold">{approvedQuotes.length} serviços fechados</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-1">
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Taxa de Fechamento</span>
                <h3 className="text-lg font-black text-brand">{conversionRate.toFixed(0)}%</h3>
                <p className="text-[10px] text-brand font-bold">Alta conversão</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-zinc-100 shadow-sm space-y-1">
                <span className="text-[10px] text-zinc-400 font-black uppercase tracking-widest block">Média por Serviço</span>
                <h3 className="text-lg font-black text-zinc-900">{formatCurrencyBRL(averageTicket)}</h3>
                <p className="text-[10px] text-zinc-500 font-medium">Média por proposta</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
